import React from "react";
import { Checkbox } from "@blueprintjs/core";
import { isEqual } from "lodash";
import { Node, Tree } from "utils/tree";
import { ILayoutProps } from "../types";
import { doHidden } from "components/Template/utils";
import { IExplorer, IBase } from "components/Template/types";
import Validate from "components/Template/Validate";
import "./Explorer.scss";

export interface IExplorerProps extends ILayoutProps {
  template: IExplorer;
  tree: Tree<IBase>;
}

const Group = (props: React.PropsWithChildren<IExplorerProps>) => {
  const { template, mutators, tree } = props;
  const { isHidden, handleHidden } = mutators;

  const isIndeterminate = (v: Node<IBase>) => {
    const temp = v.children.map((n) => (n.data ? isHidden(n.data.name) : false)).filter((h) => h);
    return temp.length > 0 && temp.length < v.children.length;
  };

  const handleChange = (v: Node<IBase>) => () => {
    if (handleHidden && v.data) {
      switch (template.variant) {
        case "list":
          handleHidden([v.data.name], !isHidden(v.data.name));
          break;
        case "tree":
          const show = isHidden(v.data.name);
          const paths = [v.data.name];
          const showParents = (a: Node<IBase>) => {
            if (a.parent?.data && a.parent.data?.name !== tree.root.children?.[0]?.data?.name) {
              paths.push(a.parent.data?.name);
              showParents(a.parent);
            }
          };
          const hideParents = (a: Node<IBase>) => {
            if (a.parent?.data && a.parent.data?.name !== tree.root.children?.[0]?.data?.name) {
              const temp = a.parent.children
                .map((n) => (n.data && !isHidden(n.data.name) ? n.data.name : ""))
                .filter((n) => n);
              if (isEqual(temp, [a.data?.name])) {
                paths.push(a.parent.data?.name);
                hideParents(a.parent);
              }
            }
          };
          const addChildren = (a: Node<IBase>) => {
            a.children.forEach((c) => {
              if (c.data) {
                paths.push(c.data.name);
              }
              addChildren(c);
            });
          };
          if (show) {
            showParents(v);
            addChildren(v);
            handleHidden(paths, false);
          } else {
            hideParents(v);
            addChildren(v);
            handleHidden(paths, true);
          }
          break;
        default:
      }
    }
  };

  const renderCheckbox = (v: Node<IBase>, i: number): React.ReactNode | undefined => {
    return (
      v.data && (
        <Checkbox
          key={`${v.data.name}`}
          style={{ marginLeft: `${(i - 1) * 10}px` }}
          label={v.data.label}
          checked={!isHidden(v.data.name)}
          indeterminate={isIndeterminate(v)}
          onChange={handleChange(v)}
        />
      )
    );
  };

  const renderGroup = (v: Node<IBase>, i = 0): React.ReactNode | undefined => {
    if (!v.data) {
      return;
    }
    return (
      <React.Fragment key={`${v.data.name}`}>
        {v.data.type !== "explorer" && renderCheckbox(v, i)}
        {v.children.map((c) => (c.children.length > 0 ? renderGroup(c, i + 1) : renderCheckbox(c, i + 1)))}
      </React.Fragment>
    );
  };

  if (doHidden(props)) {
    return null;
  }

  return (
    <Validate {...props} label={template.label} showInfo>
      <div id={template.name} className="tree">
        <div>{renderGroup(tree.root.children?.[0])}</div>
        <div>{props.children}</div>
      </div>
    </Validate>
  );
};

export default Group;
